import { CloseOutlined, PlusOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Checkbox, ConfigProvider, Input } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CreateLabelRequest } from '../../../api/types/label.types';
import { addLabel } from '../../../api/workspace';
import colorTokens from '../../../constants/design_system/colors';
import { StatusColor } from '../../../constants/design_system/statusColors';
import { useIsAllTasksView } from '../../../hooks/useIsAllTasksView';
import { SpaceSettingsTab } from '../../../pages/Space/SpaceSettingsModal/SpaceSettingsModal';
import { useGetActualSpaceId } from '../../../serverState/workspace';
import { useSpaceSettingsStore } from '../../../stores/spaceSettingsStore';
import { SpaceLabel } from '../../../types/TaskLabel';
import Workspace from '../../../types/Workspace';
import LabelChip, { LabelChipType } from '../LabelChip';
import LabelOverflowPill from '../LabelOverflowPill';
import CustomTooltip from '../Tooltip/CustomTooltip';
import BaseDropdown from './BaseDropdown';
import './ItemDropdown.scss';
import NoDragWrapper from './NoDragWrapper';

export interface LabelDropdownProps {
  selectedLabels: SpaceLabel[];
  availableLabels: SpaceLabel[];
  onSelectionChange: (labels: SpaceLabel[]) => void;
  onBlur: () => void;
  disabled?: boolean;
  size: 'small' | 'medium' | 'large';
  hideOutline?: boolean;
  defaultOpen?: boolean;
  emptyElement?: React.ReactNode;
  className?: string;
  placeholder?: string;
  position?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'topCenter' | 'topLeft' | 'topRight';
  customAlign?: boolean;
  /**
   * Keep the popup from moving sideways while labels are toggled (#3098). By default a
   * popup that would overflow on the right is flipped to align with the trigger's RIGHT
   * edge, and the trigger here is as wide as its chips, so every toggle moved the popup.
   * With this set it is shifted just enough to stay in view instead, which does not
   * depend on the trigger's width. Vertical flipping is unchanged.
   */
  shiftHorizontally?: boolean;
}

// rc-trigger merges `align` over the placement's builtin align shallowly, so `overflow`
// here replaces antd's `{ adjustX: true, adjustY: true }` for bottomLeft as a whole.
const SHIFT_HORIZONTALLY_OVERFLOW = { adjustX: false, shiftX: true, adjustY: true };

const LabelDropdown = (props: LabelDropdownProps) => {
  const [isDropdownOpen, setDropdownOpen] = useState(props.defaultOpen || false);
  const [scrollKey, setScrollKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  // Local state for immediate UI updates (optimistic)
  const [localSelectedLabels, setLocalSelectedLabels] = useState<SpaceLabel[]>(props.selectedLabels);
  const isDropdownOpenRef = useRef(isDropdownOpen);
  isDropdownOpenRef.current = isDropdownOpen;
  const queryClient = useQueryClient();
  const spaceId = useGetActualSpaceId();
  const isAllTasksView = useIsAllTasksView();
  // Manage Labels routes into a single space's settings, so it must be hidden in
  // the cross-space All Tasks view (#2683). spaceId alone is not enough: it is the
  // persisted last-visited space and stays set inside All Tasks.
  const canManageLabels = !!spaceId && !isAllTasksView;
  const requestOpenSpaceSettings = useSpaceSettingsStore((state) => state.requestOpen);
  const searchInputRef = useRef<any>(null);
  // Signature of the last selection we pushed to the parent — used to avoid a
  // redundant re-commit on dropdown close after an immediate commit (DEV-2297).
  const lastCommittedRef = useRef<string>(JSON.stringify(props.selectedLabels));
  // Set briefly when a checkbox is toggled. Applying the label re-renders (and in
  // the table view can re-mount) the overlay content; the antd Dropdown can then
  // read the just-clicked checkbox as detached and fire a spurious "outside click"
  // close. We swallow that one close so the dropdown stays open (DEV-2606).
  const justToggledRef = useRef(false);

  // Commit the selection to the parent immediately (checkbox toggle, label-text
  // click, remove-all, create-label) instead of only on dropdown close.
  const commitSelection = (newSelection: SpaceLabel[]) => {
    setLocalSelectedLabels(newSelection);
    lastCommittedRef.current = JSON.stringify(newSelection);
    props.onSelectionChange(newSelection);
  };

  // Sync local state with props when they change externally,
  // but NOT while the dropdown is open (user is actively editing)
  useEffect(() => {
    if (!isDropdownOpenRef.current) {
      setLocalSelectedLabels(props.selectedLabels);
      lastCommittedRef.current = JSON.stringify(props.selectedLabels);
    }
  }, [props.selectedLabels]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      // Small delay to ensure dropdown is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isDropdownOpen]);

  // Label creation mutation
  const createLabelMutation = useMutation({
    mutationFn: async (labelData: CreateLabelRequest) => {
      if (!spaceId) throw new Error('No workspace selected');
      const response = await addLabel(Number(spaceId), labelData);
      return response.data;
    },
    onSuccess: (newLabel: SpaceLabel) => {
      // Update the workspace query cache to include the new label
      queryClient.setQueryData(['workspace', Number(spaceId)], (oldData: Workspace | undefined) => {
        if (oldData?.Labels) {
          return {
            ...oldData,
            Labels: [...oldData.Labels, newLabel],
          };
        }
        return oldData;
      });
    },
  });

  // Checkbox click: toggle the label, keep the dropdown open, apply immediately.
  const handleLabelToggle = (label: SpaceLabel) => {
    const isSelected = localSelectedLabels.some((l) => l.id === label.id);
    const newSelection = isSelected
      ? localSelectedLabels.filter((l) => l.id !== label.id)
      : [...localSelectedLabels, label];
    // Guard against the spurious close that a mutation-triggered re-render/re-mount
    // can provoke right after this click (DEV-2606). Cleared on the next tick so a
    // genuine outside click still closes normally.
    justToggledRef.current = true;
    setTimeout(() => {
      justToggledRef.current = false;
    }, 0);
    commitSelection(newSelection);
  };

  // Label-text click: toggle the label's selection, apply, then close the dropdown.
  // Clicking a selected label unselects it (single-select shortcut, 6694).
  // Programmatically closing the BaseDropdown does NOT fire onOpenChange, so we
  // commit explicitly here rather than relying on the close handler.
  const handleLabelTextClick = (label: SpaceLabel) => {
    const isSelected = localSelectedLabels.some((l) => l.id === label.id);
    const newSelection = isSelected
      ? localSelectedLabels.filter((l) => l.id !== label.id)
      : [...localSelectedLabels, label];
    commitSelection(newSelection);
    setSearchTerm('');
    setDropdownOpen(false);
    props.onBlur?.();
  };

  const handleRemoveLabels = () => {
    commitSelection([]);
  };

  // Sort labels with selected ones on top only when dropdown opens
  // Uses props.selectedLabels (not local) so sorting doesn't change while clicking
  const sortedLabels = useMemo(() => {
    return props.availableLabels.sort((a, b) => {
      // Only sort selected labels to top when dropdown opens
      const aIsSelected = props.selectedLabels.some((l) => l.id === a.id);
      const bIsSelected = props.selectedLabels.some((l) => l.id === b.id);

      if (aIsSelected && !bIsSelected) return -1; // a comes first
      if (!aIsSelected && bIsSelected) return 1; // b comes first

      // If both have same selection status, sort by orderIndex
      return a.orderIndex - b.orderIndex;
    });
  }, [isDropdownOpen, props.availableLabels]);

  // Filter the pre-sorted labels based on search term
  const filteredLabels = sortedLabels.filter((label) => label.title.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Manage Labels via Space Settings. Only rendered when spaceId is set (i.e. not
  // in the cross-space All Tasks view, #2683 — that view has no single space
  // settings, so it has no Manage Labels entry point at all).
  const manageLabels = () => {
    requestOpenSpaceSettings(SpaceSettingsTab.LABELS);
    setDropdownOpen(false);
    props.onBlur();
  };

  // Create and assign new label
  const handleCreateLabel = async () => {
    if (!searchTerm.trim()) return;

    try {
      const newLabel = await createLabelMutation.mutateAsync({
        title: searchTerm.trim(),
        color: StatusColor.grey,
        orderIndex: 0, // Place at top
      });

      // Automatically assign the new label, committing immediately to prevent a
      // race condition (if user closes dropdown before this async op completes).
      commitSelection([...localSelectedLabels, newLabel]);

      setSearchTerm('');
    } catch (error) {
      console.error('Failed to create label:', error);
    }
  };

  // Check if search term matches any existing label exactly
  const exactMatch = filteredLabels.find((label) => label.title.toLowerCase() === searchTerm.toLowerCase());

  // Show create option when there's search text and no exact match
  const showCreateOption = searchTerm.trim().length > 0 && !exactMatch;

  return (
    <NoDragWrapper className="h-full">
      <CustomTooltip title={props.disabled ? 'Labels cannot be changed for this item.' : ''}>
        <BaseDropdown
          disableTransition
          trigger={['click']}
          open={isDropdownOpen}
          disabled={props.disabled}
          placement={props.position || 'bottomLeft'}
          align={
            props.customAlign || props.shiftHorizontally
              ? {
                  ...(props.customAlign ? { offset: [-24, 4] } : {}),
                  ...(props.shiftHorizontally ? { overflow: SHIFT_HORIZONTALLY_OVERFLOW } : {}),
                }
              : undefined
          }
          onOpenChange={(open) => {
            // A checkbox toggle just re-rendered the overlay; ignore the resulting
            // phantom close so multi-select via checkboxes keeps the dropdown open
            // in every context, including the table view (DEV-2606).
            if (!open && justToggledRef.current) return;
            if (open) setScrollKey((k) => k + 1);
            setDropdownOpen(open);
            if (!open) {
              // Dropdown closing (e.g. outside click). Selection is already applied
              // immediately on each change, so only commit here if something is still
              // uncommitted — guard against re-firing a selection we just pushed.
              const sig = JSON.stringify(localSelectedLabels);
              const hasChanged = sig !== JSON.stringify(props.selectedLabels) && sig !== lastCommittedRef.current;
              if (hasChanged) {
                lastCommittedRef.current = sig;
                props.onSelectionChange(localSelectedLabels);
              }
              setSearchTerm('');
              if (props.onBlur) {
                props.onBlur();
              }
            }
          }}
          dropdownRender={() => (
            <div
              key={scrollKey}
              className="flex flex-col items-start justify-start bg-brand-white shadow-md rounded-lg px-2 max-h-[350px] overflow-y-auto"
            >
              {/* Search input */}
              <div className="w-full mb-2 sticky top-0 z-10 bg-brand-white pt-2">
                <Input
                  ref={searchInputRef}
                  placeholder="Search for Label"
                  prefix={<SearchOutlined className="mr-1" />}
                  allowClear={searchTerm.length > 0}
                  className="[&_input::placeholder]:!text-neutral-900 px-2 py-1 text-neutral-900 mr-auto w-[200px]"
                  value={searchTerm}
                  onChange={handleSearch}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
              </div>

              {/* Available labels */}
              {filteredLabels.map((label) => (
                <SelectableLabelItem
                  key={label.id}
                  label={label}
                  isSelected={localSelectedLabels.some((l) => l.id === label.id)}
                  onToggle={() => handleLabelToggle(label)}
                  onSelect={() => handleLabelTextClick(label)}
                />
              ))}

              {/* Show message when no results */}
              {filteredLabels.length === 0 && !searchTerm.trim() && (
                <div className="justify-start text-neutral-900 text-xs font-semibold py-2">No Labels Available</div>
              )}

              {filteredLabels.length === 0 && searchTerm.trim() && !showCreateOption && (
                <div className="justify-start text-neutral-900 text-xs font-semibold py-2">No Results</div>
              )}

              <div className="w-full mb-1"></div>

              {/* Footer actions: Manage Labels (only outside All Tasks, #2683), create
                  (while searching) or remove (with a selection). Each carries its own
                  divider so no stray separator is left. */}
              {(canManageLabels ||
                showCreateOption ||
                (localSelectedLabels.length > 0 && searchTerm.trim().length === 0)) && (
                <div className="w-full sticky bottom-0 bg-brand-white pb-2">
                  {/* Create label option */}
                  {showCreateOption && (
                    <>
                      <div className="w-full h-px bg-neutral-300 mb-1"></div>
                      <div
                        className="w-full py-2 px-1 flex items-center justify-start gap-2 cursor-pointer hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={createLabelMutation.isPending ? undefined : handleCreateLabel}
                      >
                        <PlusOutlined className="text-neutral-900" />
                        <span className="text-neutral-900 text-sm">
                          {createLabelMutation.isPending ? 'Creating...' : `Create \u201c${searchTerm}\u201d`}
                        </span>
                      </div>
                    </>
                  )}
                  {localSelectedLabels.length > 0 && searchTerm.trim().length === 0 && (
                    <>
                      <div className="w-full h-px bg-neutral-300 mb-1"></div>
                      <div
                        className="w-full py-2 px-1 text-neutral-900 text-sm cursor-pointer hover:bg-neutral-200 flex items-center gap-2"
                        onClick={handleRemoveLabels}
                      >
                        <CloseOutlined className="text-neutral-900" />
                        {localSelectedLabels.length > 1 ? 'Remove Labels' : 'Remove Label'}
                      </div>
                    </>
                  )}
                  {canManageLabels && !showCreateOption && (
                    <>
                      <div className="w-full h-px bg-neutral-300 mb-1"></div>
                      <div
                        className="w-full py-2 px-1 text-neutral-900 text-sm cursor-pointer hover:bg-neutral-200 flex items-center gap-2"
                        onClick={manageLabels}
                      >
                        <SettingOutlined className="text-neutral-900" />
                        Manage Labels
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        >
          <div
            className={`${props.className} flex items-center cursor-pointer`}
            style={{ outline: isDropdownOpen && !props.hideOutline ? `2px solid ${colorTokens.green[300]}` : 'none' }}
          >
            {localSelectedLabels.length > 0 ? (
              <div className="w-full h-[28px] flex justify-start items-center gap-1">
                {localSelectedLabels[0] && (
                  <LabelChip
                    key={localSelectedLabels[0].id}
                    label={localSelectedLabels[0]}
                    type={LabelChipType.Table}
                  />
                )}
                <LabelOverflowPill hiddenLabels={localSelectedLabels.slice(1)} />
              </div>
            ) : (
              <div className="w-full h-[28px] flex items-center justify-start">
                {props.emptyElement && props.emptyElement}
                {!props.emptyElement && (
                  <div className="text-neutral-700 px-2 h-[28px] flex items-center">
                    {props.placeholder || 'Add Label'}
                  </div>
                )}
              </div>
            )}
          </div>
        </BaseDropdown>
      </CustomTooltip>
    </NoDragWrapper>
  );
};

// Component for selectable label items in dropdown.
// Two distinct interactive zones (DEV-2297):
//  - checkbox zone -> toggles selection, keeps dropdown open, applies immediately
//  - label-text zone -> selects + applies, then closes the dropdown
export const SelectableLabelItem = (props: {
  label: SpaceLabel;
  isSelected: boolean;
  onToggle: () => void;
  onSelect: () => void;
  isPending?: boolean;
}) => {
  return (
    // Compact row with a single hover highlight spanning the whole row (DEV-2605).
    <div className="w-[192px] flex items-center justify-start gap-2 px-1 py-0.5 rounded cursor-pointer hover:bg-neutral-200">
      <ConfigProvider
        theme={{
          components: {
            Checkbox: {
              colorPrimary: colorTokens.blue[600],
              colorPrimaryHover: colorTokens.blue[700],
            },
          },
        }}
      >
        <div
          className="flex items-center"
          onClick={(e) => {
            e.stopPropagation();
            if (!props.isPending) props.onToggle();
          }}
        >
          <Checkbox checked={props.isSelected} disabled={props.isPending} />
        </div>
      </ConfigProvider>
      <div
        className="flex-1 flex items-center"
        onClick={(e) => {
          e.stopPropagation();
          if (!props.isPending) props.onSelect();
        }}
      >
        <LabelChip label={props.label} type={LabelChipType.Dropdown} />
      </div>
    </div>
  );
};

export default LabelDropdown;
